/* ============================================================================
 * THE_SCHOOL — cohorts that learn the rails
 * ----------------------------------------------------------------------------
 * Structure ported from the design source (SupercomputeA/supercompute-immersion
 * → app-school.html): the Projects · immersion NFT / School / Presence events
 * tabs, the three tracks of three lessons with progress bars, the cohort table,
 * the session sheet. What the design source fills with "sample data" is either
 * backed by something real here or labelled for what it is — nothing on this
 * page is dressed up as a running programme when no cohort exists.
 *
 * WHAT IS REAL ON THIS PAGE
 *   • Lesson ticks and progress. Held in this browser; the bar is the only
 *     progress report the desk keeps.
 *   • The certifying schema (PresenceEvent, its UID, its chain) and whether the
 *     registry has it registered — asked live, answered honestly.
 *   • The role gate, evaluated against the local role matrix (marked PROPOSED ·
 *     NOT ENFORCED, because no roles contract exists).
 *
 * WHAT IS NOT REAL, AND SAYS SO
 *   • Cohorts and sessions are a SPECIMEN SHEET: a planned shape, not enrolment.
 *   • No certificate is issued. Issuing needs the resolver deployed (Phase A5).
 *   • Configuration changes nothing outside this browser.
 * ========================================================================= */

import { useEffect, useMemo, useState } from 'react';
import {
  ROLES,
  hasPermission,
  type PermissionKey,
} from '../lib/roles';
import { PRESENCE_CHAIN } from '../lib/presence-chain';
import { PRESENCE_EVENT, proposedSchemaUID } from '../lib/eas';
import { deriveEventId } from '../lib/presence';
import { shortUid } from '../lib/eas-index';
import { useSchemaRegistration } from '../hooks/useEasReads';

/* ── curriculum ───────────────────────────────────────────────────────────── */

interface Track {
  id: string;
  label: string;
  /** Role capability required to enrol on this track. */
  gate: PermissionKey;
  lessons: { id: string; title: string }[];
}

const TRACKS: readonly Track[] = [
  {
    id: 'web3',
    label: 'Web3 basics',
    gate: 'SCHOOL_ENROLL',
    lessons: [
      { id: 'w1', title: 'Keys and custody' },
      { id: 'w2', title: 'Rails and settlement' },
      { id: 'w3', title: 'What a wallet is' },
    ],
  },
  {
    id: 'security',
    label: 'Security',
    gate: 'SCHOOL_ENROLL',
    lessons: [
      { id: 's1', title: 'Signing is forever' },
      { id: 's2', title: 'Recognising a drain' },
      { id: 's3', title: 'Custody under pressure' },
    ],
  },
  {
    id: 'wallet',
    label: 'Wallet',
    gate: 'SCHOOL_ENROLL',
    lessons: [
      { id: 'l1', title: 'Connect, read, revoke' },
      { id: 'l2', title: 'Session keys and permissions' },
      { id: 'l3', title: 'Recovery without a keeper' },
    ],
  },
] as const;

const ALL_LESSONS = TRACKS.flatMap((t) => t.lessons);

/* ── specimen sheet: the planned shape, not enrolment ─────────────────────── */

const COHORTS = [
  {
    id: '01',
    note: 'Presence event, Lisbon',
    track: 'web3 basics',
    seats: 40,
    minted: 0,
    state: 'open' as const,
  },
  {
    id: '02',
    note: 'Online, weekly',
    track: 'security',
    seats: 80,
    minted: 0,
    state: 'holding' as const,
  },
];

const SESSIONS = [
  {
    name: 'Keys and custody',
    track: 'web3 basics',
    mode: 'live room',
    led: 'desk-led',
    state: 'planned' as const,
  },
  {
    name: 'Signing is forever',
    track: 'security',
    mode: 'online',
    led: 'desk-led',
    state: 'planned' as const,
  },
  {
    name: 'Connect, read, revoke',
    track: 'wallet',
    mode: 'online',
    led: 'operator-led',
    state: 'holding' as const,
  },
];

/* ── local configuration ──────────────────────────────────────────────────── */

interface SchoolConfig {
  cohort: string;
  seats: number;
  track: string;
  mode: 'in person' | 'online' | 'hybrid';
  gate: PermissionKey;
  credentialOnClose: boolean;
}

const DEFAULT_CONFIG: SchoolConfig = {
  cohort: 'Cohort 01',
  seats: 40,
  track: 'web3',
  mode: 'hybrid',
  gate: 'SCHOOL_ENROLL',
  credentialOnClose: true,
};

const CONFIG_KEY = 'supercompute-webos:school.config';
const TAUGHT_KEY = 'supercompute-webos:school.taught';

function readLocal<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeLocal(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked — in-memory state still renders */
  }
}

const TABS = [
  { id: 'curriculum', label: 'Curriculum' },
  { id: 'cohorts', label: 'Cohorts · immersion NFT' },
  { id: 'sessions', label: 'Presence events' },
  { id: 'credentials', label: 'Credentials' },
  { id: 'config', label: 'Configuration' },
  { id: 'about', label: 'About the school' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/* ── the desk ─────────────────────────────────────────────────────────────── */

export function TheSchool() {
  const [tab, setTab] = useState<TabId>('curriculum');
  const [roles, setRoles] = useState<string[]>([]);
  const [taught, setTaught] = useState<string[]>(() =>
    readLocal<string[]>(TAUGHT_KEY, []),
  );
  const [config, setConfig] = useState<SchoolConfig>(() =>
    readLocal<SchoolConfig>(CONFIG_KEY, DEFAULT_CONFIG),
  );
  const [selectedTrack, setSelectedTrack] = useState<string>(TRACKS[0].id);

  useEffect(() => writeLocal(TAUGHT_KEY, taught), [taught]);
  useEffect(() => writeLocal(CONFIG_KEY, config), [config]);

  const canEnrol = (gate: PermissionKey) =>
    roles.length > 0 && hasPermission(roles, gate);

  /* Is the certifying schema registered yet? The same read the Embassy makes. */
  const presenceUid = useMemo(() => proposedSchemaUID(PRESENCE_EVENT), []);
  const { read: presenceSchema } = useSchemaRegistration(
    PRESENCE_CHAIN.chainId,
    presenceUid,
  );

  const track = useMemo(
    () => TRACKS.find((t) => t.id === selectedTrack) ?? TRACKS[0],
    [selectedTrack],
  );

  /**
   * The PresenceEvent a certificate for this track would be issued against.
   * Derived locally from the name, location and a timestamp — it is a preview
   * of the id, not an attestation that exists.
   */
  const certPreview = useMemo(
    () => ({
      eventName: `Web3 School — ${track.label}`,
      location: config.mode === 'online' ? 'virtual' : 'supercompute-hq',
      eventId: deriveEventId(
        `Web3 School — ${track.label}`,
        config.mode === 'online' ? 'virtual' : 'supercompute-hq',
        BigInt(Math.floor(Date.now() / 1000)),
      ),
    }),
    [track, config.mode],
  );

  const taughtCount = ALL_LESSONS.filter((l) => taught.includes(l.id)).length;
  const curriculumPct = Math.round(
    (taughtCount / ALL_LESSONS.length) * 100,
  );

  const toggleLesson = (id: string) =>
    setTaught((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  return (
    <div className="dsp page">
      {/* ── masthead — the desk carries its own crest ────────────────────── */}
      <header className="page__head">
        <img
          className="page__crest"
          src="/logos/dock-projects-school.png"
          width={40}
          height={40}
          alt=""
          aria-hidden="true"
        />
        <div className="page__titles">
          <p className="eyebrow">projects · school · presence</p>
          <h1 className="h1">Cohorts that learn the rails</h1>
        </div>
        <div className="page__chips">
          <span className="badge badge-admin">
            {taughtCount} of {ALL_LESSONS.length} lessons
          </span>
        </div>
      </header>

      <div className="tabs page__tabs" role="tablist" aria-label="School modules">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={tab === t.id}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ CURRICULUM ═════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'curriculum'}>
        {/* The design system's school banner: label · headline · ASCII-free fill
            bar · ratio. `n/9` is the whole-curriculum ratio; each track keeps its
            own fill bar below. */}
        <div className="school-banner">
          <div>
            <p className="label">school · progress</p>
            <p className="headline">
              Three tracks, nine lessons,{' '}
              <span className="gold">one credential</span>
            </p>
            <div
              className="prog-track"
              role="progressbar"
              aria-label="Lessons taught"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={curriculumPct}
            >
              <i
                className="prog-fill"
                style={{ width: `${curriculumPct}%` }}
              />
            </div>
            <p className="note">
              Tick a lesson when it is taught. The bar is the only progress report
              the desk keeps — it lives in this browser, and a ticked lesson is a
              note to yourself, not a credential.
            </p>
          </div>
          <div className="ratio">
            {taughtCount}
            <span className="dim">/{ALL_LESSONS.length}</span>
          </div>
        </div>

        <div className="row" style={{ flexWrap: 'wrap' }}>
          {ROLES.filter((r) =>
            ['student', 'member', 'community', 'fiscal', 'founder'].includes(r.id),
          ).map((r) => (
            <button
              key={r.id}
              type="button"
              className={roles.includes(r.id) ? 'btn btn--gold' : 'btn btn--ghost'}
              onClick={() =>
                setRoles((prev) =>
                  prev.includes(r.id)
                    ? prev.filter((x) => x !== r.id)
                    : [...prev, r.id],
                )
              }
            >
              {r.label}
            </button>
          ))}
          {roles.length === 0 && (
            <span className="cap">Select a role to test the enrolment gate.</span>
          )}
        </div>

        <div className="grid3">
          {TRACKS.map((t) => {
            const done = t.lessons.filter((l) => taught.includes(l.id)).length;
            const pct = Math.round((done / t.lessons.length) * 100);
            const allowed = canEnrol(t.gate);
            return (
              <div className="trackcol" key={t.id}>
                <div className="row row--between">
                  <p className="micro micro--ink">{t.label}</p>
                  <span className="mono">
                    {done} / {t.lessons.length}
                  </span>
                </div>
                <div
                  className="prog-track"
                  role="progressbar"
                  aria-label={`${t.label} progress`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                >
                  <i className="prog-fill" style={{ width: `${pct}%` }} />
                </div>
                <ul className="rows">
                  {t.lessons.map((l) => {
                    const on = taught.includes(l.id);
                    return (
                      <li
                        className={on ? 'lrow done' : 'lrow'}
                        key={l.id}
                        style={{ padding: '6px 0' }}
                      >
                        <span className="lrow__t">{l.title}</span>
                        <button
                          type="button"
                          className="toggle"
                          aria-pressed={on}
                          aria-label={`Taught: ${l.title}`}
                          onClick={() => toggleLesson(l.id)}
                        />
                      </li>
                    );
                  })}
                </ul>
                <div className="row">
                  <span className="cap">{t.gate}</span>
                  {roles.length > 0 &&
                    (allowed ? (
                      <span className="tag tag--ok">PASS</span>
                    ) : (
                      <span className="tag tag--err">BLOCKED</span>
                    ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="notice notice--brass">
          <span className="tick" aria-hidden="true" />
          <span>
            <strong>Progress is local.</strong> A ticked lesson is a note to
            yourself, not a credential. Certification is a Presence attestation the
            student holds in their own wallet — see the Credentials tab for what is
            still missing before one can be issued.
          </span>
        </div>
      </section>

      {/* ══ COHORTS ════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'cohorts'}>
        <div className="grid3">
          <div className="stat-tile">
            <p className="eyebrow">cohorts planned</p>
            <p className="val">{COHORTS.length}</p>
            <p className="sub">Opened on the sheet.</p>
          </div>
          <div className="stat-tile">
            <p className="eyebrow">seats</p>
            <p className="val">
              {COHORTS.reduce((n, c) => n + c.seats, 0)}
            </p>
            <p className="sub">Across both rooms.</p>
          </div>
          <div className="stat-tile">
            <p className="eyebrow">minted</p>
            <p className="val">
              {COHORTS.reduce((n, c) => n + c.minted, 0)}
            </p>
            <p className="sub">Immersion pieces issued here.</p>
          </div>
        </div>

        <div className="row row--between">
          <div className="grow">
            <p className="micro micro--ink">immersion NFT</p>
            <p className="cap">
              One piece per seat, issued at the end of the cohort. Nothing is minted
              until the cohort closes.
            </p>
          </div>
          <span className="badge badge-admin">specimen sheet</span>
        </div>

        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Cohort</th>
                <th>Track</th>
                <th className="r">Seats</th>
                <th className="r">Minted</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {COHORTS.map((c) => (
                <tr key={c.id}>
                  <td>
                    Cohort {c.id}
                    <span className="sub">{c.note}</span>
                  </td>
                  <td className="mono">{c.track}</td>
                  <td className="r mono">{c.seats}</td>
                  <td className="r mono">{c.minted}</td>
                  <td>
                    {c.state === 'open' ? (
                      <span className="badge badge-live">
                        open
                      </span>
                    ) : (
                      <span className="tag">holding</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="notice">
          <span className="tick" aria-hidden="true" />
          <span>
            <strong>No drop is live.</strong> The desk has no minting key and no
            cohort is enrolling. These two rows are the planned shape of the
            programme — the same sheet the design source draws — not a register of
            students.
          </span>
        </div>
      </section>

      {/* ══ SESSIONS ═══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'sessions'}>
        <div className="row row--between">
          <div className="grow">
            <p className="micro micro--ink">sessions</p>
            <p className="cap">
              The taught half of the school, run as Presence events.
            </p>
          </div>
          <span className="badge badge-admin">not scheduled</span>
        </div>

        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Session</th>
                <th>Track</th>
                <th>Mode</th>
                <th>Led by</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {SESSIONS.map((s) => (
                <tr key={s.name}>
                  <td>{s.name}</td>
                  <td className="mono">{s.track}</td>
                  <td className="mono">{s.mode}</td>
                  <td className="mono">{s.led}</td>
                  <td>
                    {s.state === 'planned' ? (
                      <span className="badge badge-beta">
                        planned
                      </span>
                    ) : (
                      <span className="tag">holding</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid3">
          <div className="stat-tile">
            <p className="eyebrow">next</p>
            <p className="val" style={{ fontSize: 15 }}>
              none
            </p>
            <p className="sub">No date is set.</p>
          </div>
          <div className="stat-tile">
            <p className="eyebrow">attendance</p>
            <p className="val">—</p>
            <p className="sub">Read from the door list when one exists.</p>
          </div>
          <div className="stat-tile">
            <p className="eyebrow">credential</p>
            <p className="val" style={{ fontSize: 15 }}>
              {presenceSchema?.status === 'ok' && presenceSchema.data
                ? 'ready'
                : 'blocked'}
            </p>
            <p className="sub">school-completion, issued at the end.</p>
          </div>
        </div>

        <p className="cap">
          A session becomes real when it exists as a PresenceEvent attendance
          attestation. Until one is scheduled and signed, this sheet shows the
          planned shape only — the design source prints a Lisbon date here, and
          this desk will not put a date on a room nobody has booked.
        </p>
      </section>

      {/* ══ CREDENTIALS ════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'credentials'}>
        <div className="row row--between">
          <div className="grow">
            <p className="micro micro--ink">certificate path — {track.label}</p>
            <p className="cap">
              Completion is not a certificate file. It is a Presence attestation the
              student holds in their own wallet, written through the same registry
              NEWS_DESK uses.
            </p>
          </div>
          <span className="badge badge-admin">
            {presenceSchema?.status === 'ok' && presenceSchema.data
              ? 'schema live'
              : 'not issued'}
          </span>
        </div>

        <div className="tblwrap">
          <table className="tbl">
            <tbody>
              <tr>
                <td>Certifying schema</td>
                <td className="mono">
                  {PRESENCE_EVENT.key}
                  <span className="sub">{PRESENCE_EVENT.schema}</span>
                </td>
              </tr>
              <tr>
                <td>Proposed UID</td>
                <td className="mono">{presenceUid}</td>
              </tr>
              <tr>
                <td>Registry says</td>
                <td>
                  {presenceSchema?.status === 'ok' ? (
                    presenceSchema.data ? (
                      <span className="badge badge-live">
                        registered on {PRESENCE_CHAIN.chainName}
                      </span>
                    ) : (
                      <span className="tag tag--warn">
                        nothing at that UID — schemas register in Phase A5
                      </span>
                    )
                  ) : presenceSchema?.status === 'error' ? (
                    <span className="tag tag--err">
                      registry unreachable · {presenceSchema.reason}
                    </span>
                  ) : (
                    <span className="tag tag--muted">checking…</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Chain</td>
                <td className="mono">
                  {PRESENCE_CHAIN.chainName} ({PRESENCE_CHAIN.chainId})
                </td>
              </tr>
              <tr>
                <td>Resolver</td>
                <td>
                  <span className="tag tag--warn">
                    {PRESENCE_CHAIN.resolverAddress}
                  </span>
                  <span className="sub">
                    canon literal — not a deployed address
                  </span>
                </td>
              </tr>
              <tr>
                <td>Event preview</td>
                <td className="mono">
                  {certPreview.eventId}
                  <span className="sub">
                    keccak256 of “{certPreview.eventName}” · {certPreview.location} ·
                    now — a preview of the id, not an attestation UID
                  </span>
                </td>
              </tr>
              <tr>
                <td>Gate</td>
                <td>
                  {config.gate}{' '}
                  {roles.length > 0 ? (
                    canEnrol(config.gate) ? (
                      <span className="tag tag--ok">PASS</span>
                    ) : (
                      <span className="tag tag--err">BLOCKED</span>
                    )
                  ) : (
                    <span className="tag tag--muted">select a role</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Issued</td>
                <td>
                  <span className="tag tag--warn">none — 0 issued</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="notice notice--warn">
          <span className="tick" aria-hidden="true" />
          <span>
            <strong>This desk will not show a certificate that does not exist.</strong>{' '}
            Nothing is issued: the resolver is still{' '}
            <code>{PRESENCE_CHAIN.resolverAddress}</code>, PresenceEvent is
            unregistered, and there is no write path from this browser.
          </span>
        </div>
      </section>

      {/* ══ CONFIGURATION ══════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'config'}>
        <div className="row row--between">
          <div className="grow">
            <p className="micro micro--ink">school configuration</p>
            <p className="cap">
              How the next cohort is shaped. Held in this browser — this configures
              the desk, it does not configure anything on chain.
            </p>
          </div>
          <span className="badge badge-admin">local only</span>
        </div>

        <div className="grid2">
          <div className="stack">
            <div>
              <label className="lbl micro" htmlFor="sCohort">
                Cohort name
              </label>
              <input
                className="field"
                id="sCohort"
                type="text"
                value={config.cohort}
                onChange={(e) =>
                  setConfig({ ...config, cohort: e.target.value })
                }
              />
            </div>
            <div>
              <label className="lbl micro" htmlFor="sSeats">
                Seats
              </label>
              <input
                className="field"
                id="sSeats"
                type="number"
                min={1}
                max={5000}
                value={config.seats}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    seats: Math.max(1, Number(e.target.value) || 1),
                  })
                }
              />
            </div>
            <div>
              <label className="lbl micro" htmlFor="sTrack">
                Track
              </label>
              <select
                className="field"
                id="sTrack"
                value={config.track}
                onChange={(e) => {
                  setConfig({ ...config, track: e.target.value });
                  setSelectedTrack(e.target.value);
                }}
              >
                {TRACKS.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="stack">
            <div>
              <label className="lbl micro" htmlFor="sMode">
                Mode
              </label>
              <select
                className="field"
                id="sMode"
                value={config.mode}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    mode: e.target.value as SchoolConfig['mode'],
                  })
                }
              >
                <option value="in person">in person</option>
                <option value="online">online</option>
                <option value="hybrid">hybrid</option>
              </select>
            </div>
            <div>
              <label className="lbl micro" htmlFor="sGate">
                Enrolment gate
              </label>
              <select
                className="field"
                id="sGate"
                value={config.gate}
                onChange={(e) =>
                  setConfig({ ...config, gate: e.target.value as PermissionKey })
                }
              >
                <option value="SCHOOL_ENROLL">SCHOOL_ENROLL</option>
                <option value="SCHOOL_AUTHOR">SCHOOL_AUTHOR</option>
                <option value="MEMBER_JOIN">MEMBER_JOIN</option>
              </select>
              <span className="cap">
                A predicate over the local role matrix — not an access check.
              </span>
            </div>
            <div className="term row row--between">
              <div>
                <p className="micro micro--ink">Issue credential on close</p>
                <p className="cap">
                  Would issue school-completion when the cohort ends.
                </p>
              </div>
              <button
                type="button"
                className="switch"
                role="switch"
                aria-checked={config.credentialOnClose}
                aria-pressed={config.credentialOnClose}
                aria-label="Issue credential on close"
                onClick={() =>
                  setConfig({
                    ...config,
                    credentialOnClose: !config.credentialOnClose,
                  })
                }
              >
                <span className="switch__track">
                  <span className="switch__knob" />
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="term">
          <dl className="kv">
            <dt>cohort</dt>
            <dd>
              {config.cohort || '—'} · {config.seats} seats
            </dd>
            <dt>track</dt>
            <dd>
              {TRACKS.find((t) => t.id === config.track)?.label ?? '—'}
            </dd>
            <dt>mode</dt>
            <dd>{config.mode}</dd>
            <dt>gate</dt>
            <dd className="mono">{config.gate}</dd>
            <dt>credential on close</dt>
            <dd>{config.credentialOnClose ? 'yes' : 'no'}</dd>
            <dt>issuing path</dt>
            <dd>
              <span className="tag tag--warn">
                NOT WIRED — resolver {PRESENCE_CHAIN.resolverAddress}
              </span>
            </dd>
            <dt>preview event id</dt>
            <dd className="mono">{shortUid(certPreview.eventId, 16, 8)}</dd>
          </dl>
        </div>

        <div className="row">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setConfig(DEFAULT_CONFIG);
              setTaught([]);
            }}
          >
            Reset desk state
          </button>
          <span className="cap">
            Clears lesson ticks and configuration in this browser. Nothing on chain
            to clear.
          </span>
        </div>
      </section>

      {/* ══ ABOUT ══════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'about'}>
        {/* The school's own crest — a generated sprite, not a re-used dock tile. */}
        <img
          className="pixel plate-crest"
          src="/pixel/crest-school.png"
          width={96}
          height={96}
          alt=""
          aria-hidden="true"
        />
        <div className="stack">
          <p className="micro micro--ink">what the school is</p>
          <p className="cap" style={{ maxWidth: '64ch' }}>
            The taught half of the immersion: three short tracks that take a member
            from holding keys to operating a wallet under pressure. Every lesson is
            meant to be delivered as a session, and every completed track is meant to
            be certified by an attestation the student holds — not by a PDF, and not
            by a row in somebody&apos;s database.
          </p>
          <p className="cap" style={{ maxWidth: '64ch' }}>
            That is why the credential tab is so blunt about what is missing. A
            school that issues paper certificates needs a printer; this one needs a
            registered schema and a resolver that will accept the claim. Neither
            exists yet, so nothing is issued.
          </p>
        </div>

        <p className="micro micro--ink">the compounding loop</p>
        <ul className="tl">
          <li>
            <span className="mono mono--ink">Teach</span>
            <div className="cap">
              A session runs as a Presence event — attendance is a signed claim, not
              a headcount.
            </div>
          </li>
          <li>
            <span className="mono mono--ink">Certify</span>
            <div className="cap">
              Completing a track writes a PresenceEvent attestation to the
              student&apos;s own address. It travels with them.
            </div>
          </li>
          <li>
            <span className="mono mono--ink">Open the guild</span>
            <div className="cap">
              Certified members can be recognised across the other desks — the
              Embassy, the Community desk, and any vendor that wants to check before
              onboarding.
            </div>
          </li>
        </ul>

        <p className="micro micro--ink">status</p>
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Part</th>
                <th>State</th>
                <th>What it needs</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Curriculum</td>
                <td>
                  <span className="badge badge-admin">
                    drafted
                  </span>
                </td>
                <td className="mono">—</td>
              </tr>
              <tr>
                <td>Lesson delivery</td>
                <td>
                  <span className="tag tag--warn">not scheduled</span>
                </td>
                <td className="mono">a room and a date</td>
              </tr>
              <tr>
                <td>Certification</td>
                <td>
                  <span className="tag tag--warn">not wired</span>
                </td>
                <td className="mono">Phase A5 · schema + resolver</td>
              </tr>
              <tr>
                <td>Cohort mint</td>
                <td>
                  <span className="tag tag--warn">no drop</span>
                </td>
                <td className="mono">a deployed collection</td>
              </tr>
              <tr>
                <td>Enrolment gate</td>
                <td>
                  <span className="tag tag--warn">not enforced</span>
                </td>
                <td className="mono">a roles contract</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <p className="page__foot">
        The school is the compounding loop: teach the rails, issue the credential,
        open the guild. Nothing here is issued yet — and the desk says so on every
        tab rather than showing a certificate it cannot back.
      </p>
    </div>
  );
}
